import React from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  ListItem,
  ListItemText,
  List,
} from "@mui/material";

const Section = ({ title, children }: any) => (
  <Box mb={6}>
    <Typography variant="h5" sx={headingText}>
      {title}
    </Typography>

    <Divider sx={{ mb: 2, borderColor: "#dcdcdc" }} />

    <Box
      sx={{
        ...contentText,
        "& p": contentText,
        "& ul": {
          pl: 3,
          margin: 0,
        },
        "& li": {
          marginBottom: "6px",
          ...contentText,
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

const contentText = {
  fontSize: { xs: "14px", md: "16px" },
  lineHeight: 1.8,
  color: "#333",
};

const headingText = {
  fontWeight: 600,
  fontSize: { xs: "18px", md: "20px" },
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  color: "#1a1a1a",
};

const PrivacyPolicyBanner = () => {
  return (
    <Box
      sx={{
        backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.10)), url(${require("@assets/images/common-login-bg.png")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: { xs: 120, md: 140 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "95%", md: "95%" }, py: { xs: 4, md: 6 } }}
      >
        <Typography
          variant="h3"
          sx={{
            color: "#FFF",
            fontWeight: 800,
            fontSize: { xs: 24, md: 28 },
            lineHeight: 1.25,
          }}
        >
          Privacy Policy For Rajmarg Pravesh Mobile App
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: "14px", md: "16px" },
            lineHeight: 1.8,
            pt: 1,
          }}
        >
          This Privacy Policy outlines how the Rajmarg Pravesh application
          collects, uses, and protects your personal information.
        </Typography>
      </Container>
    </Box>
  );
};

const PrivacyPolicy = () => {
  return (
    <>
      <PrivacyPolicyBanner />
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, md: 8 },
          py: 5,
          backgroundColor: "#fafafa",
        }}
      >
        {/* Introduction */}
        <Section title="Introduction">
          <Typography component="p">
            Welcome to the Privacy Policy for Rajmarg Pravesh Application. This
            document outlines the principles and practices governing the
            collection, use, and protection of personal information by Rajmarg
            Pravesh application. The policy applies to all users of the
            application.
          </Typography>
        </Section>

        {/* User Provided Information */}
        <Section title="User Provided Information">
          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>
              The Rajmarg Pravesh application collects information like Name,
              Mobile Number, Email Address, etc. while registering into the
              application to validate the user by sending OTP to the concerned
              user Mobile Number.
            </li>

            <li>
              Please note that the user may not be able to use the features in
              the application unless you login into the Rajmarg Pravesh
              application.
            </li>

            <li>
              The Rajmarg Pravesh application may collect the user's current
              location after confirmation from the user.
            </li>

            <li>
              The users will upload the inspection related image files, videos,
              documents that are stored on our servers.
            </li>
          </Box>
        </Section>

        {/* Communication */}
        <Section title="Communication">
          <Typography component="p">
            We may use the user details, in order to send OTP through Mobile
            number, and the Notifications.
          </Typography>
        </Section>

        {/* Third Party */}
        <Section title="Access by Third Parties">
          <Typography component="p">
            We may share user information with third parties only in the ways
            that are described in this privacy statement.
          </Typography>

          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>
              We may disclose user information when required by law or in
              response to valid legal requests, such as court orders, subpoenas,
              or government regulations.
            </li>
            <li>
              We will obtain user consent via MoRTH / Concern Department before
              sharing information with third parties for purposes other than
              those described in this Privacy Policy
            </li>
          </Box>
        </Section>

        {/* Security */}
        <Section title="Data Security">
          <Typography component="p">
            The Rajmarg Pravesh application employs security measures to protect
            user personal information against unauthorised access, alteration,
            disclosure or destruction.
          </Typography>

          <Typography mt={1}>
            We use encryption and other techniques to secure data of
            transmission and storage.
          </Typography>
        </Section>

        {/* Retention */}
        <Section title="Data Retention">
          <Typography component="p">
            We retain the user's personal information only for as long as the
            user is active in the Rajmarg Pravesh application
          </Typography>

          <Typography mt={1}>
            Upon request, we will securely delete or disable user personal
            information in accordance with applicable data protection laws.
          </Typography>
        </Section>

        {/* User Rights */}
        <Section title="User Rights & Restrictions">
          <Typography component="p">
            User permission to use the application is conditioned upon the
            following Use Restrictions and Conduct Restrictions:
          </Typography>

          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>
              Users have the right to access and correct inaccuracies in their
              personal information available on the Rajmarg Pravesh application.
            </li>
            <li>Use another user’s account without permission</li>
            <li>
              Provide false or inaccurate information when registering an
              account and/or inspection.
            </li>
            <li>
              Publish or link to malicious content intended to damage or disrupt
              any user’s individuality.
            </li>
            <li>
              Use the service for any unlawful purpose or for the promotion of
              illegal activities.
            </li>
            <li>
              Make any automated use of the system, or take any action that we
              deem to impose or to potentially impose an unreasonable or
              disproportionately large load on our servers or network
              infrastructure.
            </li>
            <li>
              Interfere or attempt to interfere with the proper functioning of
              the Service.{" "}
            </li>
          </Box>
        </Section>

        {/* Disclaimer */}
        <Section title="Online Content Disclaimer">
          <Box component="ul">
            <li>
              Opinions, advice, statements or other information or content made
              available through the application, not by us, are those of their
              respective users, such users are solely responsible for such
              content.
            </li>
            <li>
              We do not guarantee the accuracy, completeness, or usefulness of
              any information on the Application and neither do we adopt nor
              endorse, nor are we responsible for, the accuracy or reliability
              of any opinion, advice, or statement made by parties other than
              ourselves.
            </li>
            <li>
              If you become aware of misuse of our Service, please contact us at{" "}
              <strong>itsection.rth@gov.in</strong>
            </li>
          </Box>
        </Section>

        {/* Updates */}
        <Section title="Policy Update">
          <Typography component="p">
            This Policy may review and update regularly and the users be
            notified if there are any changes in the policy by posting here.
          </Typography>
        </Section>

        {/* Consent */}
        <Section title="Your Consent">
          <Typography component="p">
            By using the Rajmarg Pravesh application, you are consenting to our
            handling of your information as set forth in this Policy now and as
            amended by us.
          </Typography>
        </Section>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
